import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const platformOptions = [
  { value: "instagram", labelKey: "Instagram", fallback: "Instagram" },
  { value: "tiktok", labelKey: "TikTok", fallback: "TikTok" },
  { value: "youtube", labelKey: "YouTube", fallback: "YouTube" },
  { value: "x", labelKey: "X / Twitter", fallback: "X / Twitter" },
  { value: "facebook", labelKey: "Facebook", fallback: "Facebook" },
  { value: "linkedin", labelKey: "LinkedIn", fallback: "LinkedIn" },
  { value: "general", labelKey: "General", fallback: "General" },
];

const contentTypeOptions = [
  { value: "short_video", labelKey: "Short Video", fallback: "Short Video" },
  { value: "long_video", labelKey: "Long Video", fallback: "Long Video" },
  { value: "ad", labelKey: "Ad", fallback: "Ad" },
  { value: "ugc", labelKey: "UGC", fallback: "UGC" },
  { value: "educational", labelKey: "Educational", fallback: "Educational" },
  { value: "storytelling", labelKey: "Storytelling", fallback: "Storytelling" },
];

const toneOptions = [
  { value: "bold", labelKey: "Bold", fallback: "Bold" },
  { value: "natural", labelKey: "Natural", fallback: "Natural" },
  { value: "dramatic", labelKey: "Dramatic", fallback: "Dramatic" },
  { value: "confident", labelKey: "Confident", fallback: "Confident" },
  { value: "curious", labelKey: "Curious", fallback: "Curious" },
  { value: "urgent", labelKey: "Urgent", fallback: "Urgent" },
];

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return (
            item.text ||
            item.content ||
            item.label ||
            item.title ||
            item.name ||
            JSON.stringify(item)
          );
        }
        return String(item ?? "").trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.replace(/^[-*•\d.)\s]+/, "").trim())
      .filter(Boolean);
  }

  return [];
};

const formatResultToText = (result, t) => {
  if (!result) return "";
  if (typeof result === "string") return result;

  const sections = [];

  const intro =
    result.retention_intro ||
    result.intro ||
    result.output ||
    result.content ||
    result.result ||
    result.script;

  if (intro) {
    sections.push(`${t("studioTools.retentionIntroTool.output.retentionIntro", "Retention Intro")}:\n${intro}`);
  }

  const preview =
    result.scene_preview ||
    result.preview ||
    result.opening_preview ||
    result.visual_preview;

  if (preview) {
    sections.push(`${t("studioTools.retentionIntroTool.output.previewOpeningVisual", "Preview / Opening Visual")}:\n${preview}`);
  }

  const valuePromise =
    result.value_promise ||
    result.promise ||
    result.viewer_value ||
    result.why_watch;

  if (valuePromise) {
    sections.push(`${t("studioTools.retentionIntroTool.output.valuePromise", "Value Promise")}:\n${valuePromise}`);
  }

  const deeperTheme =
    result.deeper_theme ||
    result.theme ||
    result.lesson ||
    result.core_message;

  if (deeperTheme) {
    sections.push(`${t("studioTools.retentionIntroTool.output.deeperTheme", "Deeper Theme")}:\n${deeperTheme}`);
  }

  const notes = normalizeArray(
    result.notes || result.execution_notes || result.delivery_notes || result.tips
  );

  if (notes.length) {
    sections.push(
      `${t("studioTools.retentionIntroTool.output.notes", "Notes")}:\n${notes
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const alternatives = normalizeArray(
    result.alternatives || result.variations || result.options || result.more_versions
  );

  if (alternatives.length) {
    sections.push(
      `${t("studioTools.retentionIntroTool.output.alternativeVersions", "Alternative Versions")}:\n${alternatives
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  if (!sections.length) {
    return JSON.stringify(result, null, 2);
  }

  return sections.join("\n\n");
};

const RetentionIntroTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const [theme, setTheme] = useState(getStoredTheme());
  const isDark = theme === "dark";
  const [contentType, setContentType] = useState("short_video");
  const [tone, setTone] = useState("bold");
  const [targetAudience, setTargetAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedPlatform = sharedPlatform || "general";
  const topicValue = sharedTopic || "";

  useEffect(() => {
    const syncTheme = () => setTheme(getStoredTheme());

    syncTheme();
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const styles = useMemo(() => {
    return {
      wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: 18,
        color: isDark ? "#f8fafc" : "#0f172a",
      },
      card: {
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 20,
        padding: 20,
        boxShadow: isDark
          ? "0 10px 30px rgba(0,0,0,0.22)"
          : "0 10px 30px rgba(15,23,42,0.06)",
      },
      headerRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "flex-start",
        flexWrap: "wrap",
      },
      titleWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 780,
      },
      badge: {
        alignSelf: "flex-start",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(236,72,153,0.16)" : "rgba(236,72,153,0.10)",
        color: isDark ? "#f9a8d4" : "#be185d",
        border: `1px solid ${isDark ? "rgba(236,72,153,0.24)" : "rgba(236,72,153,0.18)"}`,
      },
      title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      subtitle: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.6,
        color: isDark ? "rgba(226,232,240,0.78)" : "rgba(15,23,42,0.68)",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        marginTop: 18,
      },
      fieldWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
      },
      fullWidthField: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        gridColumn: "1 / -1",
      },
      label: {
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      input: {
        width: "100%",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: "13px 14px",
        outline: "none",
        fontSize: 14,
        boxSizing: "border-box",
      },
      textarea: {
        width: "100%",
        minHeight: 140,
        resize: "vertical",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: 14,
        outline: "none",
        fontSize: 14,
        lineHeight: 1.55,
        boxSizing: "border-box",
      },
      actionsRow: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 18,
      },
      primaryButton: {
        border: "none",
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 800,
        cursor: loading ? "not-allowed" : "pointer",
        background: loading
          ? isDark
            ? "rgba(236,72,153,0.45)"
            : "rgba(236,72,153,0.45)"
          : isDark
          ? "linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)"
          : "linear-gradient(135deg, #db2777 0%, #7c3aed 100%)",
        color: "#ffffff",
        boxShadow: loading
          ? "none"
          : isDark
          ? "0 12px 24px rgba(236,72,153,0.20)"
          : "0 12px 24px rgba(219,39,119,0.16)",
      },
      secondaryButton: {
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(30,41,59,0.82)" : "#ffffff",
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      mutedText: {
        fontSize: 13,
        color: isDark ? "rgba(226,232,240,0.68)" : "rgba(15,23,42,0.62)",
      },
      errorBox: {
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        background: isDark ? "rgba(127,29,29,0.25)" : "rgba(254,226,226,1)",
        border: `1px solid ${isDark ? "rgba(248,113,113,0.22)" : "rgba(248,113,113,0.28)"}`,
        color: isDark ? "#fecaca" : "#991b1b",
        fontSize: 14,
        lineHeight: 1.5,
      },
      resultCard: {
        background: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 18,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      },
      resultHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      },
      resultTitle: {
        margin: 0,
        fontSize: 18,
        fontWeight: 800,
      },
      resultActions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
      },
      section: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
      },
      sectionTitle: {
        margin: 0,
        fontSize: 14,
        fontWeight: 800,
        color: isDark ? "#f1f5f9" : "#0f172a",
      },
      paragraph: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.75,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        whiteSpace: "pre-wrap",
      },
      list: {
        margin: 0,
        paddingLeft: 18,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        fontSize: 14,
        lineHeight: 1.65,
      },
      chipRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
      },
      chip: {
        padding: "7px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(51,65,85,0.88)" : "#ffffff",
        color: isDark ? "#cbd5e1" : "#334155",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      },
      outputBox: {
        borderRadius: 16,
        padding: 16,
        background: isDark ? "rgba(15,23,42,0.85)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)"}`,
      },
    };
  }, [theme, loading]);

  const formattedText = useMemo(
    () => formatResultToText(result, t),
    [result, t]
  );

  const retentionIntro =
    result?.retention_intro ||
    result?.intro ||
    result?.output ||
    result?.content ||
    result?.result ||
    result?.script ||
    "";

  const notes = useMemo(
    () =>
      normalizeArray(
        result?.notes || result?.execution_notes || result?.delivery_notes || result?.tips
      ),
    [result]
  );

  const alternatives = useMemo(
    () =>
      normalizeArray(
        result?.alternatives || result?.variations || result?.options || result?.more_versions
      ),
    [result]
  );

  const clearResults = () => {
    setResult(null);
    setError("");
    setCopied(false);
  };

  const resetAll = () => {
    setContentType("short_video");
    setTone("bold");
    setTargetAudience("");
    setGoal("");
    setContext("");
    setResult(null);
    setCopied(false);
    setError("");
    if (typeof setSharedTopic === "function") setSharedTopic("");
    if (typeof setSharedPlatform === "function") setSharedPlatform("general");
  };

  const copyResult = async () => {
    if (!formattedText) return;

    try {
      await navigator.clipboard.writeText(formattedText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError(
        t(
          "studioTools.retentionIntroTool.messages.copyFailed",
          "Could not copy the intro right now. Please try again."
        )
      );
    }
  };

  const tryPost = async (paths, payload) => {
    let lastError = null;

    for (const path of paths) {
      try {
        const response = await API.post(path, payload);
        return response?.data;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError;
  };

  const handleGenerate = async () => {
    if (!topicValue.trim()) {
      setError(
        t(
          "studioTools.retentionIntroTool.messages.topicRequired",
          "Please enter a topic before generating a retention intro."
        )
      );
      return;
    }

    setLoading(true);
    setError("");
    setCopied(false);

    const payload = {
      topic: topicValue.trim(),
      platform: selectedPlatform,
      content_type: contentType,
      tone,
      target_audience: targetAudience.trim(),
      goal: goal.trim(),
      context: context.trim(),
    };

    try {
      const data = await tryPost(
        [
          "/studio/retention-intro",
          "/studio/retention_intro",
          "/studio/retention-intros",
          "/studio/retention_intros",
        ],
        payload
      );

      const normalized =
        data?.result ||
        data?.intro ||
        data?.data ||
        data?.retention_intro ||
        data;

      setResult(normalized);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        t(
          "studioTools.retentionIntroTool.messages.generateFailed",
          "Something went wrong while generating the retention intro."
        );
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MotionSurface>
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <span style={styles.badge}>
              {t("studioTools.retentionIntroTool.hero.badge", "Retention Intro")}
            </span>
            <h2 style={styles.title}>
              {t(
                "studioTools.retentionIntroTool.hero.title",
                "Generate stronger opening seconds"
              )}
            </h2>
            <p style={styles.subtitle}>
              {t(
                "studioTools.retentionIntroTool.hero.description",
                "Create intros built to hold attention early with a strong hook, preview feel, deeper theme, and a clear reason to keep watching."
              )}
            </p>
          </div>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.retentionIntroTool.fields.topic.label", "Topic")}
            </label>
            <input
              type="text"
              value={topicValue}
              onChange={(e) => setSharedTopic?.(e.target.value)}
              placeholder={t(
                "studioTools.retentionIntroTool.fields.topic.placeholder",
                "e.g. why most creators stay stuck"
              )}
              style={styles.input}
            />
            <FieldHint
              text={t(
                "studioTools.retentionIntroTool.fields.topic.hint",
                "This tool works best when the topic is sharp and specific."
              )}
            />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.retentionIntroTool.fields.platform.label", "Platform")}
            </label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSharedPlatform?.(e.target.value)}
              style={styles.input}
            >
              {platformOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(
                    `studioTools.retentionIntroTool.platformOptions.${option.value}`,
                    option.fallback
                  )}
                </option>
              ))}
            </select>
            <FieldHint
              text={t(
                "studioTools.retentionIntroTool.fields.platform.hint",
                "Platform context helps shape the pacing and structure of the intro."
              )}
            />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.retentionIntroTool.fields.contentType.label", "Content Type")}
            </label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              style={styles.input}
            >
              {contentTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(
                    `studioTools.retentionIntroTool.contentTypeOptions.${option.value}`,
                    option.fallback
                  )}
                </option>
              ))}
            </select>
            <FieldHint
              text={t(
                "studioTools.retentionIntroTool.fields.contentType.hint",
                "Choose the type of content you want the opening built for."
              )}
            />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.retentionIntroTool.fields.tone.label", "Tone")}
            </label>
            <select value={tone} onChange={(e) => setTone(e.target.value)} style={styles.input}>
              {toneOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(
                    `studioTools.retentionIntroTool.toneOptions.${option.value}`,
                    option.fallback
                  )}
                </option>
              ))}
            </select>
            <FieldHint
              text={t(
                "studioTools.retentionIntroTool.fields.tone.hint",
                "Tone affects the emotional pull and voice of the intro."
              )}
            />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t(
                "studioTools.retentionIntroTool.fields.targetAudience.label",
                "Target Audience"
              )}
            </label>
            <input
              type="text"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder={t(
                "studioTools.retentionIntroTool.fields.targetAudience.placeholder",
                "e.g. beginner creators, founders, students"
              )}
              style={styles.input}
            />
            <FieldHint
              text={t(
                "studioTools.retentionIntroTool.fields.targetAudience.hint",
                "Optional audience context helps the intro feel more targeted."
              )}
            />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.retentionIntroTool.fields.goal.label", "Goal")}
            </label>
            <input
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t(
                "studioTools.retentionIntroTool.fields.goal.placeholder",
                "e.g. increase watch time, boost retention, drive curiosity"
              )}
              style={styles.input}
            />
            <FieldHint
              text={t(
                "studioTools.retentionIntroTool.fields.goal.hint",
                "Optional. Useful when you want the intro optimized for a specific outcome."
              )}
            />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>
              {t("studioTools.retentionIntroTool.fields.extraContext.label", "Extra Context")}
            </label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder={t(
                "studioTools.retentionIntroTool.fields.extraContext.placeholder",
                "Describe the content idea, story angle, lesson, product, or emotional direction..."
              )}
              style={styles.textarea}
            />
            <FieldHint
              text={t(
                "studioTools.retentionIntroTool.fields.extraContext.hint",
                "Add context when you want the opening to feel more tailored and accurate."
              )}
            />
          </div>
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading
              ? t("studioTools.retentionIntroTool.actions.generating", "Generating...")
              : t("studioTools.retentionIntroTool.actions.generateIntro", "Generate Intro")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={clearResults}
          >
            {t("studioTools.retentionIntroTool.actions.clearResults", "Clear Results")}
          </UtilityButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={resetAll}
          >
            {t("studioTools.retentionIntroTool.actions.reset", "Reset")}
          </UtilityButton>
        </ActionRow>

        <div style={{ marginTop: 10 }}>
          <span style={styles.mutedText}>
            {t(
              "studioTools.retentionIntroTool.tip",
              "Tip: great retention intros usually create tension fast, preview value early, and make the viewer feel they should not scroll away."
            )}
          </span>
        </div>

        {error ? <div style={styles.errorBox}>{error}</div> : null}
      </div>

      {loading ? (
        <ToolResultSkeleton isDark={theme === "dark"} count={3} />
      ) : result ? (
        <div style={styles.card}>
          <ResultCard colors={{ border: theme === "dark" ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)", cardBgStrong: theme === "dark" ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)", shadowSoft: theme === "dark" ? "0 10px 24px rgba(2,6,23,0.22)" : "0 6px 18px rgba(15,23,42,0.05)" }} index={0}>
            <div style={styles.resultHeader}>
              <h3 style={styles.resultTitle}>
                {t("studioTools.retentionIntroTool.output.resultTitle", "Retention Intro Result")}
              </h3>

              <ActionRow style={styles.resultActions}>
                <CopyButton
                  type="button"
                  copied={copied}
                  onClick={copyResult}
                >
                  {copied
                    ? t("studioTools.retentionIntroTool.actions.copied", "Copied")
                    : t("studioTools.retentionIntroTool.actions.copy", "Copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="retention_intro"
                  title={`Retention Intro${topicValue ? ` - ${topicValue}` : ""}`}
                  topic={topicValue || "Retention Intro"}
                  platform={selectedPlatform}
                  content={formattedText}
                  meta={{
                    contentType,
                    tone,
                    targetAudience,
                    goal,
                    context,
                  }}
                />
              </ActionRow>
            </div>

            <div style={styles.chipRow}>
              <span style={styles.chip}>
                {t(
                  `studioTools.retentionIntroTool.platformOptions.${selectedPlatform}`,
                  platformOptions.find((item) => item.value === selectedPlatform)?.fallback || "General"
                )}
              </span>
              <span style={styles.chip}>
                {t(
                  `studioTools.retentionIntroTool.contentTypeOptions.${contentType}`,
                  contentTypeOptions.find((item) => item.value === contentType)?.fallback ||
                    "Short Video"
                )}
              </span>
              <span style={styles.chip}>
                {t(
                  `studioTools.retentionIntroTool.toneOptions.${tone}`,
                  toneOptions.find((item) => item.value === tone)?.fallback || "Bold"
                )}
              </span>
              {topicValue ? <span style={styles.chip}>{topicValue}</span> : null}
            </div>

            {retentionIntro ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.retentionIntroTool.output.retentionIntro", "Retention Intro")}
                </h4>
                <div style={styles.outputBox}>
                  <p style={styles.paragraph}>{retentionIntro}</p>
                </div>
              </div>
            ) : null}

            {result?.scene_preview ||
            result?.preview ||
            result?.opening_preview ||
            result?.visual_preview ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t(
                    "studioTools.retentionIntroTool.output.previewOpeningVisual",
                    "Preview / Opening Visual"
                  )}
                </h4>
                <p style={styles.paragraph}>
                  {result?.scene_preview ||
                    result?.preview ||
                    result?.opening_preview ||
                    result?.visual_preview}
                </p>
              </div>
            ) : null}

            {result?.value_promise ||
            result?.promise ||
            result?.viewer_value ||
            result?.why_watch ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.retentionIntroTool.output.valuePromise", "Value Promise")}
                </h4>
                <p style={styles.paragraph}>
                  {result?.value_promise ||
                    result?.promise ||
                    result?.viewer_value ||
                    result?.why_watch}
                </p>
              </div>
            ) : null}

            {result?.deeper_theme ||
            result?.theme ||
            result?.lesson ||
            result?.core_message ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.retentionIntroTool.output.deeperTheme", "Deeper Theme")}
                </h4>
                <p style={styles.paragraph}>
                  {result?.deeper_theme ||
                    result?.theme ||
                    result?.lesson ||
                    result?.core_message}
                </p>
              </div>
            ) : null}

            {notes.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.retentionIntroTool.output.notes", "Notes")}
                </h4>
                <ul style={styles.list}>
                  {notes.map((item, index) => (
                    <li key={`note-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {alternatives.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t(
                    "studioTools.retentionIntroTool.output.alternativeVersions",
                    "Alternative Versions"
                  )}
                </h4>
                <ul style={styles.list}>
                  {alternatives.map((item, index) => (
                    <li key={`alternative-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!formattedText ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.retentionIntroTool.output.rawResult", "Raw Result")}
                </h4>
                <p style={styles.paragraph}>{JSON.stringify(result, null, 2)}</p>
              </div>
            ) : null}
          </ResultCard>
        </div>
      ) : null}
    </div>
    </MotionSurface>
  );
};

export default RetentionIntroTool;